import {
    Button,
    Chip,
    Container,
    FormControl,
    FormControlLabel,
    FormLabel,
    Icon,
    Link,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import generatePDF, { Margin } from "react-to-pdf";
import { useAppSelector } from "../../../hooks/reduxHooks";

export default function Result() {
    const { stepQuestions } = useAppSelector(
        (state) => state.customerDataSlice
    );

    const mainContentRef = useRef<HTMLDivElement>(null);
    const targetRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        let el = document.getElementById("1");
        if (el) {
            el.style.marginTop = "3cm";
        }
        el = document.getElementById("2");
        if (el) {
            el.style.marginTop = "7cm";
        }
    }, []);

    const percent = useMemo(() => {
        const answers = stepQuestions.flatMap((q) => q.answers);
        const trueAnswers = answers.filter((a) => a.answer === true);
        const number = Math.round((trueAnswers.length / answers.length) * 100);
        return new Intl.NumberFormat("fa-IR").format(number);
    }, []);

    const handleRedirect = () => {
        navigate("/register");
    };

    const answerToQuestion = (stepIndex: number, index: number) => {
        const step = stepQuestions.find((a) => a.step === stepIndex)!;
        return step.answers.find((a) => a.index === index)?.answer ?? null;
    };

    return (
        <Container
            sx={{ pt: 5 }}
            maxWidth="md"
        >
            <Stack
                gap={2}
                ref={mainContentRef}
            >
                <Typography
                    component="h2"
                    fontSize="large"
                >
                    نقشه‌ی راه فروش شما
                </Typography>
                <Typography component="p">
                    تبریک می‌گیم، شما برنده‌ی مسیر فروش هستید و حالا می‌دونید
                    برای فروش بیشتر و بهتر، باید چه کارا کنید. چک‌لیست نقشه‌ی
                    راه شما آماده‌س و می‌تونید دانلودش کنید.
                </Typography>
                <Stack
                    direction="row"
                    alignItems="center"
                >
                    <Typography component="p">
                        امتیاز شما در فرایند فروش:
                    </Typography>
                    <Chip
                        color="primary"
                        label={`${percent} ٪`}
                    />
                </Stack>

                <Button
                    onClick={() =>
                        generatePDF(targetRef, {
                            filename: "map.pdf",
                            page: {
                                margin: Margin.MEDIUM,
                            },
                        })
                    }
                    variant="contained"
                    size="small"
                    sx={{ width: "fit-content", m: "auto" }}
                    id="download-map"
                >
                    دانلود چک‌لیست نقشه‌ی راه من
                </Button>

                <Typography component="p">
                    حالا شما نیاز به تمرین بیشتر و البته اطلاعات پروژه‌های
                    ساختمانی (مثل بانک اطلاعات ساختمان پرشین سازه) و ابزارهای
                    تکمیلی مثل سی-آر-ام، پنل تبلیغات پیامکی، اتوماسیون و …
                    دارید. ما در پرشین سازه همه‌ی این‌ها رو برای شما فراهم کردیم
                    و در آکادمی پرشین سازه رایگان کمک‌تون می‌کنیم تا مهارت‌های
                    فروش خودتون رو تقویت کنید. برای درخواست مشاوره‌ی رایگان
                    کافیه روی دکمه‌ی زیر بزنید و ما باهاتون تماس می‌گیریم.
                </Typography>
                <Button
                    variant="contained"
                    color="success"
                    size="large"
                    sx={{ width: "fit-content", margin: "auto" }}
                    onClick={() => handleRedirect()}
                    id="free-demo-button"
                >
                    من می‌خوام که بیشتر بفروشم | دموی رایگان
                </Button>

                <Stack
                    spacing={1}
                    alignItems={"center"}
                    justifyContent={"center"}
                    direction={"row"}
                >
                    <Typography>شماره تماس: </Typography>
                    <Link href="tel:02172897000">۰۲۱-۷۲۸۹۷۰۰۰</Link>
                </Stack>
            </Stack>

            <Stack
                ref={targetRef}
                sx={{
                    position: "absolute",
                    transform: "translateX(-200%)",
                    padding: 5,
                    width: "21cm",
                }}
                spacing={2}
            >
                <Stack sx={{ height: 120, width: "fit-content" }}>
                    <img
                        width={105}
                        height={105}
                        style={{ margin: "auto" }}
                        src="/images/logo_light.png"
                        alt="پرشین سازه"
                    />
                    <Typography
                        component="h1"
                        fontSize="40px"
                    >
                        پرشین سازه
                    </Typography>
                </Stack>

                <span></span>
                <span></span>
                <span></span>

                <Typography
                    component="h3"
                    fontSize="40px"
                >
                    یک مسیر ساده اما حرفه‌ای برای فروشنده‌های حرفه‌ای صنعت
                    ساختمان
                </Typography>

                {stepQuestions.map((question, index) => (
                    <Stack
                        spacing={2}
                        key={index}
                    >
                        <Typography
                            sx={{ pageBreakInside: "avoid" }}
                            id={index.toString()}
                            component="h3"
                            fontSize="30px"
                        >
                            {question.title}: {question.label}
                        </Typography>
                        {question.questions.map((q, i) => (
                            <FormControl key={q}>
                                <FormLabel sx={{ fontSize: "25px" }}>
                                    {q}
                                </FormLabel>
                                <RadioGroup
                                    value={answerToQuestion(question.step, i)}
                                >
                                    <FormControlLabel
                                        value={true}
                                        control={
                                            <Radio
                                                checkedIcon={
                                                    <Icon color="success">
                                                        check
                                                    </Icon>
                                                }
                                            />
                                        }
                                        label={
                                            <Typography fontSize="25px">
                                                بله
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        value={false}
                                        control={
                                            <Radio
                                                checkedIcon={
                                                    <Icon color="warning">
                                                        error
                                                    </Icon>
                                                }
                                            />
                                        }
                                        label={
                                            <Typography fontSize="25px">
                                                خیر
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>
                        ))}
                    </Stack>
                ))}

                <Typography
                    component="p"
                    fontSize="30px"
                >
                    تبریک می‌گیم، شما برنده‌ی مسیر فروش هستید و حالا می‌دونید
                    برای فروش بیشتر و بهتر، باید چه کارا کنید.
                </Typography>
                <Typography
                    component="p"
                    fontSize="30px"
                >
                    حالا شما نیاز به تمرین بیشتر و البته اطلاعات پروژه‌های
                    ساختمانی (مثل بانک اطلاعات ساختمان پرشین سازه) و ابزارهای
                    تکمیلی مثل سی-آر-ام، پنل تبلیغات پیامکی، اتوماسیون و …
                    دارید. ما در پرشین سازه همه‌ی این‌ها رو برای شما فراهم کردیم
                    و در آکادمی پرشین سازه رایگان کمک‌تون می‌کنیم تا مهارت‌های
                    فروش خودتون رو تقویت کنید. برای درخواست مشاوره رایگان از
                    طریق شماره زیر با ما تماس بگیرید.
                </Typography>

                <Stack
                    direction="row"
                    justifyContent="center"
                    spacing={2}
                >
                    <Typography
                        fontSize="30px"
                        fontWeight="bold"
                        textAlign="center"
                    >
                        شماره تماس:
                    </Typography>
                    <Typography
                        fontSize="30px"
                        fontWeight="bold"
                        textAlign="center"
                    >
                        ۰۲۱-۷۲۸۹۷۰۰۰
                    </Typography>
                </Stack>
                <Stack
                    sx={{ height: 120 }}
                    alignItems={"center"}
                >
                    <img
                        width={105}
                        height={105}
                        style={{ margin: "auto" }}
                        src="/images/logo_light.png"
                        alt="پرشین سازه"
                    />
                    <Typography
                        component="h1"
                        fontSize="40px"
                    >
                        پرشین سازه
                    </Typography>
                </Stack>
            </Stack>
        </Container>
    );
}
